import { useEffect, useState } from 'react';
import { STRINGS } from '@/strings/strings';

interface SeriesPlanLoadingProps {
  /** Drives the per-world loading video + text colour (aa = navy, kp = white). */
  world: 'aa' | 'kp';
}

/**
 * Loading screen for the "Staffel planen" wait. Mirrors the single-story
 * generation loading visual (per-world video + gold progress bar + percent),
 * but is a self-contained presentational sibling: the single-story screen is
 * inlined in OutputRoute and driven by an SSE story-job stream, which the plan
 * call (one ~16s POST, no job/SSE) does not have. Rather than refactor — and
 * thereby touch — single-story code, this renders the same assets with a
 * synthetic progress curve. The parent unmounts it when the plan resolves.
 */
export function SeriesPlanLoading({ world }: SeriesPlanLoadingProps) {
  const textColor = world === 'kp' ? 'text-dream-white' : 'text-dream-navy';

  // Synthetic progress: ease toward ~95% over the typical ~16-18s plan wait.
  // It never reaches 100 on its own — the parent swaps to the plan view when
  // the response lands. Mirrors the single-story bar's feel without its SSE.
  const [percent, setPercent] = useState(4);
  useEffect(() => {
    const id = setInterval(() => {
      setPercent((p) => (p >= 95 ? 95 : p + (95 - p) * 0.04));
    }, 200);
    return () => clearInterval(id);
  }, []);
  const shown = Math.floor(percent);

  return (
    <div className="flex flex-col items-center pt-6 pb-4">
      <div className="mb-8 flex aspect-video w-[90vw] max-w-[420px] items-center justify-center md:w-[480px] md:max-w-none desktop:w-[560px]">
        <video
          src={`/videos/loading-${world}.mp4`}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden
          className="h-full w-full object-contain"
        />
      </div>

      <p className={`mb-4 text-center text-base ${textColor}`}>
        {STRINGS.series.builder.planLoading}
      </p>

      <div
        className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-dream-navy/15"
        role="progressbar"
        aria-valuenow={shown}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-dream-gold transition-[width] duration-200 ease-linear"
          style={{ width: `${shown}%` }}
        />
      </div>

      <p className={`mt-2 text-sm font-medium ${textColor}`}>{shown}%</p>
    </div>
  );
}
